import {createTheme} from "@mui/material/styles";
import {colors, fonts, scrim, shadows} from "./tokens";

const numeric = {fontVariantNumeric: "tabular-nums"} as const;

export const adminTheme = createTheme({
    palette: {
        primary: {main: colors.primary, dark: colors.primaryHover, contrastText: "#fff"},
        secondary: {main: colors.chrome},
        error: {main: colors.error},
        warning: {main: colors.warning},
        success: {main: colors.success},
        info: {main: colors.info},
        text: {primary: colors.ink, secondary: colors.muted, disabled: colors.placeholder},
        background: {default: colors.canvas, paper: colors.card},
        divider: colors.cardBorder,
        action: {hover: colors.rowHover, selected: colors.rowHover},
    },
    shape: {borderRadius: 4},
    typography: {
        fontFamily: fonts.sans,
        fontSize: 13,
        h4: {fontSize: "20px", fontWeight: 500, lineHeight: 1.3, color: colors.ink},
        h5: {fontSize: "22px", fontWeight: 500, lineHeight: 1.3, color: colors.ink},
        h6: {fontSize: "14px", fontWeight: 500, lineHeight: 1.4, color: colors.ink},
        subtitle1: {fontSize: "15px", fontWeight: 500, lineHeight: 1.4},
        subtitle2: {fontSize: "12.5px", fontWeight: 400, color: colors.muted},
        body1: {fontSize: "13px", lineHeight: 1.5},
        body2: {fontSize: "12.5px", lineHeight: 1.5},
        caption: {fontSize: "11.5px", lineHeight: 1.4},
        overline: {fontSize: "10px", fontWeight: 600, letterSpacing: "1.4px", lineHeight: 1.6},
        button: {fontSize: "13px", fontWeight: 500, textTransform: "none"},
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {backgroundColor: colors.canvas, color: colors.body, WebkitFontSmoothing: "antialiased"},
                a: {color: colors.primary},
            },
        },
        MuiAppBar: {
            defaultProps: {elevation: 0},
            styleOverrides: {root: {backgroundColor: colors.chrome, color: "#fff"}},
        },
        MuiPaper: {
            styleOverrides: {
                root: {backgroundImage: "none"},
                outlined: {borderColor: colors.cardBorder, boxShadow: shadows.card},
                elevation1: {boxShadow: shadows.card, border: `1px solid ${colors.cardBorder}`},
                elevation8: {boxShadow: shadows.dropdown},
            },
        },
        MuiCard: {
            defaultProps: {variant: "outlined"},
        },
        MuiButton: {
            defaultProps: {disableElevation: true},
            styleOverrides: {
                root: {minHeight: 32, paddingTop: 4, paddingBottom: 4, paddingLeft: 12, paddingRight: 12, borderRadius: 4},
                sizeSmall: {minHeight: 28, fontSize: "12.5px"},
                sizeLarge: {minHeight: 38, fontSize: "13.5px"},
                containedPrimary: {
                    boxShadow: shadows.primaryButton,
                    "&:hover": {backgroundColor: colors.primaryHover, boxShadow: shadows.primaryButton},
                },
                outlined: {borderColor: colors.inputBorder, color: colors.body, backgroundColor: "#fff"},
                outlinedPrimary: {
                    color: colors.body,
                    borderColor: colors.inputBorder,
                    "&:hover": {borderColor: colors.primary, backgroundColor: colors.rowHover},
                },
                outlinedError: {color: colors.error, borderColor: colors.errorPanelBorder},
                containedError: {backgroundColor: colors.error},
            },
        },
        MuiIconButton: {
            styleOverrides: {root: {borderRadius: 4}},
        },
        MuiInputLabel: {
            defaultProps: {shrink: true},
            styleOverrides: {
                root: {
                    position: "relative",
                    transform: "none",
                    fontSize: "12px",
                    lineHeight: 1.4,
                    color: colors.muted,
                    marginBottom: 4,
                    maxWidth: "100%",
                    "&.Mui-focused": {color: colors.muted},
                    "&.Mui-error": {color: colors.error},
                },
                asterisk: {color: colors.error},
            },
        },
        MuiOutlinedInput: {
            defaultProps: {notched: false},
            styleOverrides: {
                root: {
                    backgroundColor: "#fff",
                    fontSize: "13px",
                    borderRadius: 4,
                    "& .MuiOutlinedInput-notchedOutline": {borderColor: colors.inputBorder, top: 0},
                    "& .MuiOutlinedInput-notchedOutline legend": {display: "none"},
                    "&:hover .MuiOutlinedInput-notchedOutline": {borderColor: colors.faint},
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {borderColor: colors.primary, borderWidth: 2},
                    "&.Mui-error .MuiOutlinedInput-notchedOutline": {borderColor: colors.error, borderWidth: 1},
                    "&.Mui-disabled": {backgroundColor: colors.tableHeader},
                    "&.Mui-readOnly": {backgroundColor: colors.tableHeader},
                },
                input: {
                    padding: "8px 10px",
                    "&::placeholder": {color: colors.placeholder, opacity: 1},
                },
                inputSizeSmall: {padding: "7px 10px"},
                multiline: {padding: 0},
                inputMultiline: {padding: "8px 10px"},
            },
        },
        MuiFormHelperText: {
            styleOverrides: {root: {fontSize: "11.5px", marginLeft: 0, marginTop: 4}},
        },
        MuiFormControlLabel: {
            styleOverrides: {label: {fontSize: "13px"}},
        },
        MuiAutocomplete: {
            styleOverrides: {
                inputRoot: {"&.MuiOutlinedInput-root": {paddingTop: 0, paddingBottom: 0, paddingLeft: 0}},
                input: {"&.MuiAutocomplete-input": {padding: "7px 10px"}},
            },
        },
        MuiSelect: {
            styleOverrides: {select: {minHeight: 0}},
        },
        MuiMenu: {
            styleOverrides: {paper: {boxShadow: shadows.dropdown, border: `1px solid ${colors.cardBorder}`}},
        },
        MuiMenuItem: {
            styleOverrides: {root: {fontSize: "13px", minHeight: 34}},
        },
        MuiTableContainer: {
            styleOverrides: {root: {overflowX: "auto"}},
        },
        MuiTableCell: {
            styleOverrides: {
                root: {fontSize: "13px", padding: "8px 12px", borderBottom: `1px solid ${colors.rowDivider}`, ...numeric},
                head: {
                    height: 36,
                    paddingTop: 0,
                    paddingBottom: 0,
                    backgroundColor: colors.tableHeader,
                    color: colors.muted,
                    fontSize: "11px",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    whiteSpace: "nowrap",
                    borderBottom: `1px solid ${colors.cardBorder}`,
                },
                sizeSmall: {padding: "6px 12px"},
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    "&.MuiTableRow-hover:hover": {backgroundColor: colors.rowHover},
                    "&.Mui-selected, &.Mui-selected:hover": {
                        backgroundColor: colors.rowHover,
                        boxShadow: `inset 3px 0 0 ${colors.primary}`,
                    },
                },
            },
        },
        MuiTableSortLabel: {
            styleOverrides: {
                root: {"&.Mui-active": {color: colors.ink}},
                icon: {fontSize: "14px"},
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {height: 20, borderRadius: 3, fontSize: "11.5px", fontWeight: 500},
                label: {paddingLeft: 7, paddingRight: 7},
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {borderRadius: 4, boxShadow: shadows.dialog, border: "none"},
            },
        },
        MuiBackdrop: {
            styleOverrides: {root: {"&:not(.MuiBackdrop-invisible)": {backgroundColor: scrim}}},
        },
        MuiDialogTitle: {
            styleOverrides: {root: {fontSize: "18px", fontWeight: 500, color: colors.ink, padding: "18px 20px 8px"}},
        },
        MuiDialogContent: {
            styleOverrides: {root: {padding: "8px 20px 16px"}},
        },
        MuiDialogActions: {
            styleOverrides: {root: {padding: "12px 20px 16px", gap: 8, borderTop: `1px solid ${colors.sectionDivider}`}},
        },
        MuiSwitch: {
            styleOverrides: {
                root: {width: 32, height: 18, padding: 0, margin: "0 8px", overflow: "visible"},
                switchBase: {
                    padding: 2,
                    "&.Mui-checked": {
                        transform: "translateX(14px)",
                        color: "#fff",
                        "& + .MuiSwitch-track": {backgroundColor: colors.primary, opacity: 1},
                    },
                    "&.Mui-disabled + .MuiSwitch-track": {opacity: 0.4},
                },
                thumb: {width: 14, height: 14, boxShadow: "none"},
                track: {borderRadius: 9, backgroundColor: colors.switchTrackOff, opacity: 1},
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {fontSize: "13px", borderRadius: 4, alignItems: "flex-start"},
                standardError: {
                    backgroundColor: colors.errorPanelBackground,
                    color: colors.errorPanelText,
                    border: `1px solid ${colors.errorPanelBorder}`,
                },
                standardSuccess: {backgroundColor: colors.successTint, color: colors.success},
                standardWarning: {backgroundColor: colors.warningTint, color: colors.warningText},
                standardInfo: {backgroundColor: colors.infoTint, color: colors.info},
            },
        },
        MuiSnackbarContent: {
            styleOverrides: {root: {backgroundColor: colors.ink, fontSize: "13px", borderRadius: 4}},
        },
        MuiTooltip: {
            styleOverrides: {tooltip: {backgroundColor: colors.ink, fontSize: "11.5px"}},
        },
        MuiAccordion: {
            defaultProps: {disableGutters: true, elevation: 0},
            styleOverrides: {
                root: {
                    border: `1px solid ${colors.cardBorder}`,
                    borderRadius: 4,
                    boxShadow: shadows.card,
                    "&:before": {display: "none"},
                },
            },
        },
        MuiLink: {
            styleOverrides: {root: {color: colors.primary, "&:hover": {color: colors.primaryHover}}},
        },
        MuiListItemButton: {
            styleOverrides: {root: {borderRadius: 4}},
        },
    },
});
